import React from "react";
import { Card, CardBody, CardHeader, Listbox, ListboxItem, Switch } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import { useBrand } from "@webuild-ai/react-auth";

import {
  useUIPreferences,
  EXPORT_FONT_OPTIONS,
  EXPORT_PAGE_SIZE_OPTIONS,
  type ExportFontOption,
  type ExportPageSize,
} from "@/stores/UIPreferencesStore";
import { DEFAULT_FONT, isSystemSafeFont } from "@/utils/fontUtils";

export const ExportSettings: React.FC = () => {
  const { t } = useTranslation();
  const {
    exportFontFamily,
    setExportFontFamily,
    exportPageSize,
    setExportPageSize,
    showAdvancedDownloadOptions,
    toggleAdvancedDownloadOptions,
  } = useUIPreferences();
  const { brand } = useBrand();

  const handleFontChange = (keys: "all" | Set<React.Key>) => {
    const selectedKey = Array.from(keys)[0] as ExportFontOption;

    if (selectedKey) {
      setExportFontFamily(selectedKey);
    }
  };

  const handlePageSizeChange = (keys: "all" | Set<React.Key>) => {
    const selectedKey = Array.from(keys)[0] as ExportPageSize;

    if (selectedKey) {
      setExportPageSize(selectedKey);
    }
  };

  /**
   * Get display label for a font option
   * For "brand" option, shows the actual brand font name
   */
  const getDisplayLabel = (option: (typeof EXPORT_FONT_OPTIONS)[0]): string => {
    if (option.value === "brand") {
      const brandFont = brand.fontFamily || DEFAULT_FONT;

      return `${t("Use Brand Font")} (${brandFont})`;
    }

    return option.label;
  };

  /**
   * Get the effective font that will be used for exports.
   * Resolves "brand" to the actual brand font or default.
   */
  const getEffectiveFont = (): string => {
    if (exportFontFamily === "brand") {
      return brand.fontFamily || DEFAULT_FONT;
    }

    return exportFontFamily;
  };

  /**
   * Check if the currently selected font is system-safe.
   * Used to determine whether to show compatibility warning.
   */
  const isCurrentFontSystemSafe = isSystemSafeFont(getEffectiveFont());

  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Icon icon="solar:export-bold" width={24} />
          <span>{t("Export Settings")}</span>
        </h2>
      </CardHeader>
      <CardBody className="space-y-6">
        {/* Export Font Selector */}
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="font-medium">{t("Export Font")}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t(
                "Select the font family used when exporting artefacts to Word, PDF, Excel, and PowerPoint",
              )}
            </p>
          </div>
          <Listbox
            aria-label={t("Select export font")}
            classNames={{
              base: "border border-default-200 rounded-lg p-1",
            }}
            data-testid="export-font-listbox"
            selectedKeys={new Set([exportFontFamily])}
            selectionMode="single"
            onSelectionChange={handleFontChange}
          >
            {EXPORT_FONT_OPTIONS.map((option) => (
              <ListboxItem
                key={option.value}
                classNames={{
                  base: "data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary",
                }}
                startContent={
                  option.value === "brand" ? (
                    <Icon className="text-primary" icon="solar:star-bold" width={16} />
                  ) : undefined
                }
                textValue={option.label}
              >
                <div className="flex items-center justify-between w-full">
                  <span>{getDisplayLabel(option)}</span>
                  {option.value === exportFontFamily && (
                    <Icon className="text-primary" icon="solar:check-circle-bold" width={18} />
                  )}
                </div>
              </ListboxItem>
            ))}
          </Listbox>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {t("This setting applies to both single artefact downloads and batch exports")}
          </p>
          {!isCurrentFontSystemSafe && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800">
              <Icon
                className="text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5"
                icon="solar:danger-triangle-bold"
                width={16}
              />
              <p className="text-xs text-warning-700 dark:text-warning-300">
                {t(
                  "This font may not be installed on the recipient's computer. The document will display a fallback font instead. For guaranteed compatibility, use Calibri, Arial, or Times New Roman.",
                )}
              </p>
            </div>
          )}
        </div>

        {/* Export Page Size Selector */}
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="font-medium">{t("Page Size")}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("Select the page size for exported Word documents")}
            </p>
          </div>
          <Listbox
            aria-label={t("Select page size")}
            classNames={{
              base: "border border-default-200 rounded-lg p-1",
            }}
            data-testid="export-page-size-listbox"
            selectedKeys={new Set([exportPageSize])}
            selectionMode="single"
            onSelectionChange={handlePageSizeChange}
          >
            {EXPORT_PAGE_SIZE_OPTIONS.map((option) => (
              <ListboxItem
                key={option.value}
                classNames={{
                  base: "data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary",
                }}
                startContent={
                  option.value === "A4" ? (
                    <Icon className="text-primary" icon="solar:star-bold" width={16} />
                  ) : undefined
                }
                textValue={t(option.labelKey)}
              >
                <div className="flex items-center justify-between w-full">
                  <span>
                    {t(option.labelKey)}{" "}
                    <span className="text-xs text-gray-400">({t(option.dimensionsKey)})</span>
                  </span>
                  {option.value === exportPageSize && (
                    <Icon className="text-primary" icon="solar:check-circle-bold" width={18} />
                  )}
                </div>
              </ListboxItem>
            ))}
          </Listbox>
        </div>

        {/* Advanced Download Options Toggle */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{t("Advanced Download Options")}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("Show additional export formats like PNG, CSV, Markdown, and HTML")}
              </p>
            </div>
            <Switch
              aria-label={t("Advanced Download Options")}
              data-testid="advanced-download-options-switch"
              isSelected={showAdvancedDownloadOptions}
              onValueChange={toggleAdvancedDownloadOptions}
            />
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
